/**
 * Workspace API Keys - Shared Types
 *
 * HMAC-based URL signing for secure access control to published content.
 * Pull-based architecture with Redis cache.
 */

// Enum for key types (single source of truth)
export enum WorkspaceKeyType {
  CONTENT = 'content',
  WEBHOOK = 'webhook',
  ANALYTICS = 'analytics',
  CONNECTION = 'connection'
}

// Derive union type from enum (for compatibility)
export type WorkspaceKeyTypeValue = `${WorkspaceKeyType}`;

// 2-Character prefix mapping (exported for backend use)
export const KEY_TYPE_PREFIX: Record<WorkspaceKeyType, string> = {
  [WorkspaceKeyType.CONTENT]: 'ct',
  [WorkspaceKeyType.WEBHOOK]: 'wh',
  [WorkspaceKeyType.ANALYTICS]: 'an',
  [WorkspaceKeyType.CONNECTION]: 'cn'
} as const;

// Interface for workspace key entity
export interface WorkspaceKey {
  id: string;
  workspaceId: string;
  keyId: string;
  keyType: WorkspaceKeyType;
  version: number;
  secretEncrypted: string;
  secretHistory: SecretHistoryEntry[];
  name?: string;
  description?: string;
  isActive: boolean;
  lastUsedAt?: Date;
  revokedAt?: Date;
  revokedReason?: string;
  createdBy: string;
  updatedBy?: string;
  createdAt: Date;
  updatedAt: Date;
}

// Secret history entry structure
export interface SecretHistoryEntry {
  version: number;
  encrypted_secret: string;
  rotated_at: string;
  grace_period_ends_at: string;
}

// DTOs for API operations
export interface GenerateKeyDto {
  keyType?: WorkspaceKeyType;
}

export interface RotateKeyDto {
  keyId: string;
}

export interface RevokeKeyDto {
  keyId: string;
  reason?: string;
}

export interface ValidateSignatureDto {
  keyId: string;
  signature: string;
  data: string;
  expiry: number;
}

/**
 * Connection access request using HMAC authentication
 * Used by Supabase Edge Functions to access connection credentials
 */
export interface ConnectionAccessDto {
  keyId: string;       // CONNECTION key ID (wsk_cn_xxx)
  signature: string;   // HMAC-SHA256(secret, "{connectionId}:{expiry}")
  expiry: number;      // Unix timestamp in SECONDS
}

// Response types

/**
 * Full key with plaintext secret (returned once during generate/rotate)
 */
export interface KeyWithSecret {
  keyId: string;
  secret: string;
  version: number;
  message: string;
}

/**
 * Key list item with masked keyId for secure display
 */
export interface KeyListItem {
  id: string;
  keyId: string;
  maskedKeyId: string;
  keyType: WorkspaceKeyType;
  name?: string;
  description?: string;
  version: number;
  isActive: boolean;
  revokedAt?: Date;
  revokedReason?: string;
  lastUsedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Active key response for SmallApp endpoint
 */
export interface ActiveKeyResponse {
  keyId: string;
  secret: string;
  version: number;
  keyType: WorkspaceKeyType;
  cacheDuration: number;
  workspaceId: string;
}

/**
 * Signature validation result
 */
export interface ValidationResult {
  valid: boolean;
  reason?: string;
}
